import React, { useCallback, useContext, useEffect, useState } from "react";
import { GetColumnsWithGroupResponse } from "../models/windows/GetColumnsWithGroup";
import MaterialIssueDetails from "../components/MaterialIssueDetails";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonLoading,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonToast,
  useIonAlert,
} from "@ionic/react";
import { useHistory, useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import MaterialIssueItemList from "../components/MaterialIssueItemList";
import { add, chevronBackOutline } from "ionicons/icons";
import OnlineStatus from "../components/OnlineStatus";
import {
  BinData,
  MaterialIssueListItem,
  PlannedListItem,
} from "../models/materialissue/MaterialIssue";
import MaterialIssueItemSearch from "../components/MaterialItemSearch";
import { MaterialIssueBody, issueMaterials } from "../api/Materials";
import { useIssueReturnManager } from "../hooks/useIssueReturnManager";
import {
  matchesAvailableItemsNotFullyIssued,
  matchesItemNotFullyIssued,
} from "../util/IssueMaterialHelper";
import { TranslationMessagesContext } from "../util/Translations";
import { getDefaultFilterForIssueScreen } from "../api/UserSetups";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import useGetPlannedListItems from "../hooks/work-order/issue-return/useGetPlannedListItems";
import { useWorkOrderStore } from "../store/wo-order";
import { useOfflineStore } from "../store/offline-mode";
import { useSettingsStore } from "../store/settings";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import useGetIssueDetailsColumns from "../hooks/work-order/issue-return/useGetIssueDetailsColumns";
import { useUserStore } from "../store/user";
import useGetEmployeesLiteList from "../hooks/user/useGetEmployeesLiteList";

export interface ItemListEntry {
  item: PlannedListItem;
  checked: boolean;
  binData: BinData[];
  issued: boolean;
  errorMessage?: string;
  isAdhoc: boolean;
  comments: string;
}

export enum PlannedListFilterType {
  All = "Display All",
  ItemsNotFullyIssued = "Display Items Not Fully Issued",
  AvailableItemsNotFullyIssued = "Display Available Items Not Fully Issued",
}

export const retrieveAccountSelections = (acc: string) => {
  if (!acc) {
    return undefined;
  }
  let decodedSelections = decodeParam(acc);
  let accObject = {} as any;
  let keys = [] as string[];
  let vals = [] as string[];
  decodedSelections.split("+").forEach((val, index) => {
    if (index % 2 === 0) {
      keys.push(val);
    } else {
      vals.push(val);
    }
  });
  keys.forEach((key, index) => {
    accObject[key] = vals[index];
  });
  return accObject;
};

const MaterialIssue: React.FC = () => {
  const { workOrderId, requisitionId, offlineIssueMaterialId } = useParams<{
    workOrderId: string;
    requisitionId: string;
    accountSelections: string;
    offlineIssueMaterialId: string;
  }>();
  const {
    employees,
    employeeId,
    storeLocation,
    itemList,
    dateCharged,
    searchOpen,
    displayWo,
    displayReq,
    accountObject,
    translations,
    showLoading,
    negativeInvCtrlVal,
    setShowLoading,
    setEmployeeId,
    setStoreLocation,
    setItemList,
    setDateCharged,
    setSearchOpen,
    setupDetails,
    onItemSelect,
    onSelectAll,
    onBinAmountChange,
    onResponse,
    onComment,
    onItemAdd,
    onNegativeErrorForItem,
  } = useIssueReturnManager();
  const history = useHistory();

  const [issueDetailsColumns, setIssueDetailsColumns] =
    useState<GetColumnsWithGroupResponse>();
  const [filter, setFilter] = useState<PlannedListFilterType>(
    PlannedListFilterType.ItemsNotFullyIssued
  );
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const negativeError =
    translatedMessages["NegativeQtyOnHandError"]?.MessageText ||
    "The current Bin Quantity is not sufficient to issue the chosen quantity of this item.";
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const { getPlannedListItems: getPlannedListItemsForIssue } =
    useGetPlannedListItems();
  const isOffline = useOfflineStore((state) => state.isOffline);
  const defaultStore = useSettingsStore((state) => state.defaultStore);
  const addIssue = useWoOfflineUpdatesStore((state) => state.addIssue);
  const updateById = useWoOfflineUpdatesStore((state) => state.updateById);
  const offlineIssue = useWoOfflineUpdatesStore((state) =>
    state.getIssueById(offlineIssueMaterialId)
  );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const updateStatusById = useWoOfflineUpdatesStore(
    (state) => state.updateStatusById
  );
  const { getIssueDetailsColumns } = useGetIssueDetailsColumns();
  const { getLiteListOfEmployees } = useGetEmployeesLiteList();
  const [inventoryConditions] = useState<
    Record<
      string,
      { automaticIssueUponReceipt: boolean; itemTableDefinition: string }
    >
  >({});

  useEffect(() => {
    setup();
  }, []);

  const filterItems = (filter: PlannedListFilterType) => {
    const uncheckFilteredItems = itemList.map((item) => {
      const condition = inventoryConditions[item.item?.ItemType];

      // Common condition check
      const isConditionValid =
        condition &&
        !condition.automaticIssueUponReceipt &&
        condition.itemTableDefinition !== "n";

      // Filter-specific logic
      const isMatch = (() => {
        switch (filter) {
          case PlannedListFilterType.All:
            return isConditionValid;

          case PlannedListFilterType.ItemsNotFullyIssued:
            return isConditionValid && matchesItemNotFullyIssued(item, filter);

          case PlannedListFilterType.AvailableItemsNotFullyIssued:
            return (
              isConditionValid &&
              matchesAvailableItemsNotFullyIssued(item, filter)
            );

          default:
            return false;
        }
      })();

      // Return item if it matches the filter, otherwise uncheck it
      return isMatch ? item : { ...item, checked: false };
    });

    setItemList(uncheckFilteredItems);
  };

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      const value =
        useWorkOrderStore.getState().defaultFilterForIssueScreen ??
        (await getDefaultFilterForIssueScreen());
      setFilter(value);
      filterItems(value);
      setShowLoading(false);
    })();
  }, [isOffline, setShowLoading]);

  const getOfflineIssueItems = useCallback(
    (items: ItemListEntry[]) => {
      if (!offlineIssue || !offlineIssue.MaterialIssueList?.length)
        return items;
      return offlineIssue.MaterialIssueList.reduce(
        (list: ItemListEntry[], curr) => {
          const item = items.find((i) => i.item.Item === curr.Item);
          if (item) {
            let updatedItem = { ...item };
            updatedItem.binData = updatedItem.binData.map((b) => {
              const updateBin = curr.Bins.find((cb) => cb.Bin === b.Bin);
              if (updateBin)
                b = {
                  ...b,
                  TransactionQuantity: updateBin.TransactionQuantity,
                };
              return b;
            });
            updatedItem = {
              ...updatedItem,
              comments: curr.Comments,
              checked: true,
            };

            list.push(updatedItem);
          }

          return list;
        },
        []
      );
    },
    [offlineIssue]
  );

  const getPlannedListItems = useCallback(
    async (storesLocation: string, workOrder: string, requisition: string) => {
      const issueItemList = useWorkOrderStore
        .getState()
        .getIssueItemForWo(displayWo);

      if (issueItemList && isOffline) {
        setItemList(getOfflineIssueItems([...issueItemList]));
        return; //got it in the cache
      }
      setShowLoading(true);
      const { response, items } = await getPlannedListItemsForIssue({
        requisition,
        storesLocation,
        workOrder,
        isIssueMaterial: true,
      });
      if(response.isError || (response.data && !response.data.Success)){
        presentAlert({
          message: response.data?.MessageList && response.data.MessageList.length > 0 && response.data.MessageList[0].Text,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
            },
          ],
        });
        setShowLoading(false);
        return;
      }
      setItemList(getOfflineIssueItems([...items]));
      setShowLoading(false);
    },
    [
      displayWo,
      getOfflineIssueItems,
      getPlannedListItemsForIssue,
      setItemList,
      setShowLoading,
    ]
  );

  useEffect(() => {
    if (storeLocation && (displayWo || displayReq)) {
      getPlannedListItems(storeLocation, displayWo, displayReq);
    } else if (isOffline && displayWo && defaultStore) {
      getPlannedListItems(defaultStore, displayWo, displayReq);
    }
  }, [
    storeLocation,
    displayWo,
    displayReq,
    getPlannedListItems,
    isOffline,
    defaultStore,
  ]);

  useEffect(() => {
    (async () => {
      if (offlineIssue) {
        //feed offline date
        setDateCharged(offlineIssue.IssueDate ?? "");
        let employees = useUserStore.getState().employees;
        if (!employees) employees = await (await getLiteListOfEmployees()).data;

        setEmployeeId(
          employees?.find((e) => e.EmployeeId === offlineIssue.IssueToUser)
        );
      }
    })();
  }, [getLiteListOfEmployees, offlineIssue, setDateCharged, setEmployeeId]);

  const setup = async () => {
    const columns = useWorkOrderStore.getState().issueColumns;
    setShowLoading(true);
    const columnDetails = columns ?? (await getIssueDetailsColumns()).data;
    setIssueDetailsColumns(columnDetails);
    setupDetails();
    setShowLoading(false);
  };

  const isReadyToIssue = () => {
    const checkedList = itemList.filter((entry) => {
      return entry.checked;
    });
    if (checkedList.length === 0) {
      return false;
    }
    let amount = 0;
    for (const entry of checkedList) {
      for (const bin of entry.binData) {
        // Do not enable the issue button if the plant setting is blocking negative values;
        if (
          bin.TransactionQuantity &&
          bin.TransactionQuantity > 0 &&
          bin.TransactionQuantity > bin.QtyOnHand &&
          negativeInvCtrlVal === "e"
        ) {
          return false;
        }
        amount += bin.TransactionQuantity || 0;
      }
    }
    return amount > 0;
  };

  const issue = async () => {
    if (!storeLocation || !employeeId) {
      return;
    }
    const materialIssueList: MaterialIssueListItem[] = [];
    const selectedItems = itemList.filter((entry) => {
      return entry.checked;
    });
    const itemsWithNegativeBin: string[] = [];
    selectedItems.forEach((entry, index) => {
      const bins = [];
      for (const bin of entry.binData) {
        if (
          bin.TransactionQuantity &&
          bin.TransactionQuantity > 0 &&
          entry.item.LocationQuantityAvailable > 0 &&
          entry.item.LocationQuantityAvailable >= bin.TransactionQuantity
        ) {
          bins.push({
            Bin: bin.Bin,
            MaterialIssueId: bin.ObjectId,
            RequisitionLine: entry.item.RequisitionLine,
            TransactionQuantity: bin.TransactionQuantity,
          });
        }
      }
      if (bins.length > 0) {
        materialIssueList.push({
          Comments: entry.comments,
          Item: entry.item.Item,
          ItemIndex: index,
          Requisition: entry.item.Requisition,
          RequisitionLine: entry.item.RequisitionLine,
          SerialIds: [],
          StoresLocation: storeLocation,
          Bins: bins,
        });
      } else {
        itemsWithNegativeBin.push(entry.item.Item);
        onNegativeErrorForItem(itemsWithNegativeBin, negativeError);
        presentToast({
          message: negativeError,
          duration: 5000,
          position: "top",
          color: "danger",
        });
      }
    });

    if (!materialIssueList.length) return; //got nothing to issue

    const body: MaterialIssueBody = {
      PlanningRequisition: !!requisitionId ? displayReq : "",
      WorkOrder: !!workOrderId ? displayWo : "",
      IssueDate: dateCharged,
      IssueToUser: employeeId?.EmployeeId,
      Account: {
        Account:
          !!accountObject && !!accountObject.Account
            ? accountObject.Account
            : "",
        Area: !!accountObject && !!accountObject.Area ? accountObject.Area : "",
        Department:
          !!accountObject && !!accountObject.Department
            ? accountObject.Department
            : "",
        Project:
          !!accountObject && !!accountObject.Project
            ? accountObject.Project
            : "",
      },
      MaterialIssueList: materialIssueList,
    };

    if (isOffline) {
      const data = { ...body, woNumber: displayWo };
      offlineIssueMaterialId
        ? updateById(offlineIssueMaterialId, data)
        : addIssue(data);
      presentAlert({
        header: `${
          translatedMessages["MobIssueMaterialSaveSuccess"]?.MessageText ||
          "Issue Material Saved Successfully In Offline Store."
        }`,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              // Navigate back, replacing so the completed issue screen is removed from history
              if (workOrderId) {
                history.replace(`/workorder/${encodeParam(displayWo)}`);
              } else {
                history.goBack();
              }
            },
          },
        ],
      });
      return; // we will sync when user is back online
    }

    setShowLoading(true);
    const response = await issueMaterials(body);
    setShowLoading(false);
    if (response.isError) {
      if (offlineIssueMaterialId)
        updateStatusById(offlineIssueMaterialId, "failed", [
          String(response.data),
        ]);
      presentToast({
        message: String(response.data),
        duration: 5000,
        color: "danger",
      });
    } else if (offlineIssueMaterialId) {
      makeOperationCompletedById(offlineIssueMaterialId); //remove from offline store
      if (workOrderId) {
        history.replace(`/workorder/${encodeParam(displayWo)}`);
      } else {
        history.goBack();
      }
    } else {
      var newItemList = [...itemList];
      let updatedItemList = [...newItemList];
      response.data.forEach((issuedData: any) => {
        if (issuedData.Success) {
          const parts = issuedData.MaterialTransactionId.split("|");
          const itemValue = parts[1];
          updatedItemList = updatedItemList.map((entry) => {
            const totalTransactionQuantity = entry.binData?.reduce(
              (sum, binData) => sum + (binData.TransactionQuantity || 0),
              0
            );
            const newItem = {
              ...entry.item,
              LocationQuantityAvailable:
                entry.item.LocationQuantityAvailable - totalTransactionQuantity,
              LocationQuantityOnHand:
                entry.item.LocationQuantityOnHand - totalTransactionQuantity,
              BinQuantity: entry.item.BinQuantity - totalTransactionQuantity,
              LineQuantityIssued:
                entry.item.LineQuantityIssued + totalTransactionQuantity,
            };
            if (
              entry.item.Item === itemValue &&
              entry.item.RequisitionLine === issuedData.ReqLine &&
              entry.checked
            ) {
              const newBinList = entry.binData?.map((binData) => {
                const qtyOnhand =
                  binData.QtyOnHand - (binData.TransactionQuantity ?? 0);
                return {
                  ...binData,
                  TransactionQuantity: 0,
                  QtyOnhand: qtyOnhand,
                };
              });
              return {
                ...entry,
                binData: newBinList,
                item: newItem,
                checked: false,
              };
            }
            return entry;
          });
        }
      });
      globalThis.fromReturn = true;
      onResponse(response.data, updatedItemList);
      onNegativeErrorForItem(itemsWithNegativeBin, negativeError);
      globalThis.fromReturn = false;
    }
  };

  const onFilterChange = (newFilter: PlannedListFilterType) => {
    if (newFilter === filter) {
      return;
    }
    setFilter(newFilter);
    filterItems(newFilter);
  };

  const onItemRemoval = (item: PlannedListItem) => {
    let itemIndex = itemList.findIndex((entry) => {
      return entry.item === item;
    });
    if (itemIndex === -1) {
      return;
    }
    let temp = [...itemList];
    temp.splice(itemIndex, 1);
    setItemList(temp);
  };

  return (
    <>
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/home" icon={chevronBackOutline} />
            </IonButtons>
            <IonTitle>
              {displayWo || displayReq || accountObject.Account}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                color="primary"
                disabled={!isReadyToIssue()}
                onClick={issue}
              >
                {translations["lbl_btn_issue"] || "Issue"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <OnlineStatus />
        </IonHeader>

        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          duration={5000}
        />

        <IonContent class="ion-padding">
          {issueDetailsColumns && (
            <MaterialIssueDetails
              columns={issueDetailsColumns}
              storeLocation={storeLocation}
              setStoreLocation={setStoreLocation}
              employeeId={employeeId}
              setEmployeeId={setEmployeeId}
              employees={employees}
              date={dateCharged}
              setDate={setDateCharged}
              mode="issue"
              displayWo={displayWo}
              displayReq={displayReq}
            />
          )}

          <div className="ion-margin-bottom"></div>

          <MaterialIssueItemList
            itemList={itemList}
            onSelect={onItemSelect}
            onSelectAll={onSelectAll}
            onBinAmountChange={onBinAmountChange}
            mode="issue"
            onFilterChange={onFilterChange}
            handleComment={onComment}
            currentFilter={filter}
            handleRemoval={!!accountObject ? onItemRemoval : undefined}
            negativeInvCtrlVal={negativeInvCtrlVal}
          />

          <IonFab slot="fixed" vertical="bottom" horizontal="end">
            <IonFabButton
              onClick={() => {
                setSearchOpen(true);
              }}
              disabled={!storeLocation}
            >
              <IonIcon icon={add}></IonIcon>
            </IonFabButton>
          </IonFab>
        </IonContent>
      </IonPage>
      {searchOpen && (
        <MaterialIssueItemSearch
          isOpen={searchOpen}
          onSelect={onItemAdd}
          onCancel={() => {
            setSearchOpen(false);
          }}
          storeLocation={storeLocation}
          issueOrReturn="issue"
        />
      )}
    </>
  );
};

export default withAITracking(reactPlugin, MaterialIssue, "MaterialIssue");
